import { computed, Injectable, signal } from '@angular/core';
import { App, AppInfo } from '@capacitor/app';
import { Device } from '@capacitor/device';
import { DeviceInfo } from '@model/device-info';
import { BaseService } from '@shared/service/base.service';
import {
  catchError,
  firstValueFrom,
  from,
  map,
  Observable,
  of,
  tap,
} from 'rxjs';

@Injectable({ providedIn: 'root' })
export class DeviceService extends BaseService {
  private _deviceInfo = signal<DeviceInfo | undefined>(undefined);

  deviceInfo = computed<DeviceInfo | undefined>(() => this._deviceInfo());

  constructor() {
    super('device');
  }

  private async buildDeviceInfo(): Promise<DeviceInfo> {
    const deviceInfo = await Device.getInfo();
    const deviceId = await Device.getId();

    let app: Pick<AppInfo, 'build' | 'version'>;
    if (deviceInfo.platform === 'web') {
      const { build, version } = await firstValueFrom(
        this.httpClient.get<Pick<AppInfo, 'build' | 'version'>>(
          'assets/appInfo.json',
        ),
      );

      app = {
        build,
        version,
      };
    } else {
      app = await App.getInfo();
    }

    return {
      appBuild: app.build,
      appVersion: app.version,
      identifier: deviceId.identifier,
      language: (await Device.getLanguageTag()).value,
      model: deviceInfo.model,
      osVersion: deviceInfo.osVersion,
      platform: deviceInfo.platform,
    };
  }

  init(): Observable<void> {
    // Get the device info.
    return from(this.buildDeviceInfo()).pipe(
      tap(deviceInfo => this._deviceInfo.set(deviceInfo)),
      // tap(deviceInfo => this._deviceIdentifier.set(deviceInfo.identifier)),
      map(() => undefined),
      // On error of init call the flow needs to continue.
      // Will return undefined so app init will not stop
      catchError(() => {
        this._deviceInfo.set(undefined);
        return of(undefined);
      }),
    );
  }
}
